/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ViewEncapsulation, Input, SimpleChanges, OnChanges } from '@angular/core';
import { assign, toUpper, trim } from 'lodash';
import { Router } from '@angular/router';
import { SensorClassification, SensorTypes, SpeedSensorClassification } from '@app/shared/enums';
import { SensorOnboardingService } from '@app/sensordaten/services/sensor-onboarding.service';
import { MeterView } from '@app/shared/models/meter-data/meter-view';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { StringUtility } from '@app/shared/string-utility';
import { SensorListObject } from '@app/energy-map/interfaces/sensor-list';
import { PrimarySensorTypes, defaultSensorTypeIcon } from '@app/shared/constants';
import { SensorTypesService } from '@app/sensordaten/services/sensor-types.service';
import { first } from 'rxjs';
import { SensorType } from '@app/sensordaten/models/sensor-type.model';

@Component({
  selector: 'app-sensor-list',
  templateUrl: './sensor-list.component.html',
  styleUrls: ['./sensor-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SensorListComponent implements OnChanges {
  @Input() sensors: SensorListObject[] = [];
  @Input() routerParams: any = {};
  public dioxide = SensorTypes.CARBONDIOXIDE.toLowerCase();
  public displayedColumns: string[] = ['sensorType', 'deviceEUI', 'locationContext', 'details'];
  public isLoading = true;
  public temperature = SensorTypes.TEMPERATURE.toLowerCase();
  private sensorTypes: SensorType[] = [];

  constructor(
    private overlayLoaderService: OverlayLoaderService,
    private router: Router,
    private sensorOnboardingService: SensorOnboardingService,
    private sensorTypesService: SensorTypesService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {
    this.sensorTypesService
      .getSensorTypes()
      .pipe(first())
      .subscribe({
        next: (sensorTypes: SensorType[]) => {
          this.sensorTypes = sensorTypes;
        },
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['sensors'].isFirstChange()) {
      this.isLoading = false;
    } else {
      if (this.sensors.length > 0) {
        this.isLoading = false;
      }
    }

    this.mapSensorImages();
  }

  public getTrashCanValue(val: number): number {
    return (val / 100) * 120;
  }

  public redirect = async (sensor: SensorListObject): Promise<void> => {
    this.overlayLoaderService.isLoading = true;
    const item = {
      sensor_type: sensor.sensor_type,
      device_eui: sensor.device_eui,
      sensor_id: sensor.sensor_id,
    };

    const municipality = this.userMunicipalitiesService.currentMunicipality.key;

    switch (toUpper(trim(item.sensor_type))) {
      case SensorTypes.GAS:
      case SensorTypes.HEAT:
      case SensorTypes.WATER:
      case SensorTypes.ELECTRICITY:
        const res = (await this.sensorOnboardingService.getMeterBySensorId(municipality, item.sensor_id).toPromise()) as any;
        const meterView = assign(new MeterView(), res[0]);
        this.router.navigate(['/verbrauch'], {
          queryParams: {
            connectionobjectnumber: meterView.connectionObjectNumber,
            guid: meterView.guid,
            returnroute: 'energiekarte',
            ...this.routerParams,
          },
          queryParamsHandling: 'merge',
        });
        this.overlayLoaderService.isLoading = false;
        break;
      default:
        const q = {
          t: item.sensor_type,
          s: item.sensor_id,
          c: SensorClassification.NONE,
          returnroute: 'energiekarte',
          ...this.routerParams,
        };

        if (StringUtility.areStringsIdentical(item.sensor_type, SensorTypes.SPEED)) {
          q.c = SpeedSensorClassification.SPEED_LIMIT;
        }

        const sensorTypeCode = item.sensor_type.toUpperCase().trim();
        const sensorDetailsVersion: string = StringUtility.hasAnyMatchInArray(PrimarySensorTypes, [sensorTypeCode]) ? 'sensor-details' : 'sensor-detailsv2';
        this.router.navigate([`/messdaten/sensordaten/${sensorDetailsVersion}`], {
          queryParams: q,
        });
        this.overlayLoaderService.isLoading = false;
        break;
    }
  };

  private mapSensorImages(): void {
    this.sensors.forEach((sensor: SensorListObject) => {
      const sensorImage = this.sensorTypes.find((sensorType: SensorType) => StringUtility.areStringsIdentical(sensorType.code, sensor.sensor_type))?.image;
      if (!!sensorImage) {
        sensor['image'] = `./assets/icons/sensorTypes/${sensorImage}`;
      } else {
        sensor['image'] = `./assets/icons/sensorTypes/${defaultSensorTypeIcon}`;
      }
    });
  }
}
